import type { Content } from 'mdast';

/**
 * Some directives are interactive on the web but hide their content in print.
 * `:::tabBoxes` renders a row of clickable tabs ("What is Plan A?", "Why did we
 * write this?", …); on paper only the empty tab labels show and all the prose
 * behind them is lost. Each tab is just a `## Heading` + content inside the
 * container, so for print we UNWRAP the directive — splice its children into the
 * flow — and the headings/paragraphs render as ordinary FAQ-style sections.
 *
 * Run this before extractDetailsToAppendices so any `:::details` nested inside
 * an unwrapped tab box still get pulled into the appendices.
 */

// Directives that are clickable/expandable on the web and would otherwise hide
// their prose in print. Each holds `## Heading` (+ optional `### subhead`) +
// content sections, which we splice back into the flow. (`:::policyList` is
// NOT unwrapped: extractDetailsToAppendices turns it into a lettered
// appendix, so it must survive until that pass.)
const UNWRAP_DIRECTIVES = new Set(['tabBoxes']);

function isUnwrappable(node: Content): boolean {
  const n = node as { type?: string; name?: string };
  return n.type === 'containerDirective' && !!n.name && UNWRAP_DIRECTIVES.has(n.name);
}

export function unwrapInteractiveDirectives(nodes: Content[]): Content[] {
  return nodes.flatMap((node): Content[] => {
    const children = (node as { children?: Content[] }).children;
    if (isUnwrappable(node)) {
      // Replace the directive with its (recursively unwrapped) children.
      return unwrapInteractiveDirectives(children ?? []);
    }
    if (Array.isArray(children)) {
      return [{ ...node, children: unwrapInteractiveDirectives(children) } as Content];
    }
    return [node];
  });
}
